import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { loginLogo } from "@/lib/brand";
import { toast, Toaster } from "sonner";

// Login runs before auth, so it can't know which tenant to brand for yet
// (tenant-specific branding shows post-login, in Sidebar). These are
// deployment-wide (one per Render+Cloudflare deploy, i.e. one per business),
// overridable without a code change — a second client gets their own env
// values, not a fork of this file. Unset, they keep MADIO's current look.
const PRODUCT_NAME = process.env.REACT_APP_PRODUCT_NAME || "CRM";
const LOGO_LETTER = (process.env.REACT_APP_LOGO_LETTER || "M").slice(0, 1).toUpperCase();
const HERO_TAGLINE = process.env.REACT_APP_HERO_TAGLINE || "Furniture · Paints · D&W";
const HERO_HEADLINE = process.env.REACT_APP_HERO_HEADLINE || "Every visitor.\nEvery quote.\nEvery deal — captured.";
const HERO_COPY = process.env.REACT_APP_HERO_COPY ||
  "Built for showrooms that move fast. Track walk-ins, manage pipelines, quote on the floor, and watch your inventory in real time.";
const HERO_IMAGE = process.env.REACT_APP_HERO_IMAGE ||
  "https://images.unsplash.com/photo-1682184805271-11671b7ecf4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const LOGO = loginLogo();
const LOGO_LIGHT = loginLogo(true);

const GSI_SRC = "https://accounts.google.com/gsi/client";
const SHARED_KEY = "madio.sharedDevice";       // this browser is a shared computer (remembered once ticked)

const readFlag = (key) => { try { return localStorage.getItem(key) === "1"; } catch { return false; } };
const takeReason = () => {
  try {
    const r = sessionStorage.getItem("madio.signedOutReason") || "";
    sessionStorage.removeItem("madio.signedOutReason");
    return r;
  } catch { return ""; }
};

/** Google's sign-in script, loaded only when the server has a Google client id (Business setup, docs/LOGIN_SECURITY.md). */
function loadGoogleScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let s = document.querySelector(`script[src="${GSI_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = GSI_SRC;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", () => reject(new Error("Google sign-in could not load")));
  });
}

export default function Login() {
  const { user, login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [googleId, setGoogleId] = useState("");
  const googleBox = useRef(null);
  const [shared, setShared] = useState(() => readFlag(SHARED_KEY));
  const sharedRef = useRef(shared);
  const [notice] = useState(takeReason);
  const rememberShared = (v) => {
    setShared(v);
    sharedRef.current = v;
    try { v ? localStorage.setItem(SHARED_KEY, "1") : localStorage.removeItem(SHARED_KEY); } catch { /* storage off */ }
  };

  // A new sign-in opens Start here again for sales logins (App.js Home).
  useEffect(() => { try { sessionStorage.removeItem("madio:start-shown"); } catch { /* storage blocked */ } }, []);
  useEffect(() => {
    if (user) nav(user.must_set_password ? "/" : (loc.state?.from || "/"), { replace: true });
  }, [user, nav, loc.state]);

  // Sign in with Google appears once the company has set up a Google client id (GET /auth/config).
  useEffect(() => {
    let alive = true;
    api.get("/auth/config", { skipCache: true })
      .then(({ data }) => { if (alive) setGoogleId(data?.google_client_id || ""); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!googleId || !googleBox.current) return;
    let cancelled = false;
    loadGoogleScript().then(() => {
      if (cancelled || !googleBox.current) return;
      window.google.accounts.id.initialize({
        client_id: googleId,
        callback: async ({ credential }) => {
          setSubmitting(true);
          setError("");
          try {
            const u = await loginWithGoogle(credential, sharedRef.current);
            toast.success(`Welcome back, ${u.name}`);
          } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Google sign-in failed");
          } finally {
            setSubmitting(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleBox.current, {
        theme: "outline", size: "large", text: "signin_with", shape: "rectangular",
        width: Math.min(googleBox.current.offsetWidth || 360, 400),
      });
    }).catch(() => { if (!cancelled) setGoogleId(""); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [googleId]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const who = identifier.trim();
    if (!who || !password) { setError("Enter your username (or email) and password."); return; }
    setSubmitting(true);
    setError("");
    try {
      const u = await login(who, password, shared);
      if (!u.must_set_password) toast.success(`Welcome back, ${u.name}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Sign-in failed");
      setPassword("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg)] relative overflow-hidden">
      <Toaster position="top-right" />

      {/* Left: hero */}
      <div className="hidden lg:flex w-1/2 relative items-end p-12">
        <img
          src={HERO_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="relative z-10 text-white max-w-md">
          {LOGO_LIGHT ? (
            <div className="mb-6">
              <img src={LOGO_LIGHT} alt="MADIO" className="h-9 w-auto" data-testid="login-logo-hero" />
              <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-white/70">{HERO_TAGLINE}</div>
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-[var(--brand)] flex items-center justify-center font-heading font-bold text-lg">{LOGO_LETTER}</div>
              <div>
                <div className="font-heading text-xl font-bold tracking-tight">{PRODUCT_NAME}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/70">{HERO_TAGLINE}</div>
              </div>
            </div>
          )}
          <h2 className="font-heading text-4xl font-bold leading-tight mb-3">
            {HERO_HEADLINE.split("\n").map((line, i) => (
              <span key={i}>{line}{i < HERO_HEADLINE.split("\n").length - 1 && <br />}</span>
            ))}
          </h2>
          <p className="text-white/80 text-sm leading-relaxed">
            {HERO_COPY}
          </p>
        </div>
      </div>

      {/* Right: login */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative">
        <div className="brand-blob w-72 h-72 bg-[var(--brand-soft)] top-10 right-10" />
        <div className="brand-blob w-64 h-64 bg-[var(--moss-soft)] bottom-20 left-10" />

        <div className="w-full max-w-md relative z-10">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            {LOGO ? <img src={LOGO} alt="MADIO" className="h-7 w-auto" data-testid="login-logo" /> : (<>
              <div className="w-10 h-10 rounded-xl bg-[var(--brand)] flex items-center justify-center text-white font-heading font-bold">{LOGO_LETTER}</div>
              <div className="font-heading text-lg font-bold">{PRODUCT_NAME}</div>
            </>)}
          </div>

          <div className="fadeup">
            <h1 className="font-heading text-3xl font-bold tracking-tight text-[var(--ink)] mb-1">Welcome back</h1>
            <p className="text-[var(--ink-2)] text-sm mb-8">Sign in with your username or email and your password.</p>
            {notice && (
              <div role="status" className="mb-5 p-3 rounded-lg bg-[var(--surface-2)] border border-[var(--border)] text-sm text-[var(--ink-2)]" data-testid="signed-out-notice">
                {notice}
              </div>
            )}
            <form onSubmit={submit} className="space-y-4" data-testid="login-form" noValidate>
              <div>
                <label htmlFor="login-id" className="block text-xs uppercase tracking-[0.16em] text-[var(--ink-3)] font-semibold mb-2">
                  Username or email
                </label>
                <input id="login-id" value={identifier} onChange={(e) => { setIdentifier(e.target.value); setError(""); }}
                       autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} autoFocus
                       className="w-full px-3 py-3 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-base sm:text-sm outline-none focus:border-[var(--brand)]"
                       data-testid="username-input" />
              </div>
              <div>
                <label htmlFor="login-password" className="block text-xs uppercase tracking-[0.16em] text-[var(--ink-3)] font-semibold mb-2">
                  Password
                </label>
                <div className="relative">
                  <input id="login-password" type={show ? "text" : "password"} value={password}
                         onChange={(e) => { setPassword(e.target.value); setError(""); }} autoComplete="current-password"
                         className="w-full px-3 py-3 pr-12 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-base sm:text-sm outline-none focus:border-[var(--brand)]"
                         data-testid="password-input" />
                  <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"}
                          className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center text-[var(--ink-3)] hover:text-[var(--ink)]">
                    {show ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                <p className="mt-2 text-xs text-[var(--ink-3)]">Still have a 4-digit PIN? Enter it here once and you will be asked to choose a password.</p>
              </div>
              <label className="flex items-start gap-2.5 text-sm text-[var(--ink-2)] cursor-pointer select-none">
                <input type="checkbox" checked={shared} onChange={(e) => rememberShared(e.target.checked)}
                       className="mt-0.5 w-4 h-4 accent-[var(--brand)]" data-testid="shared-device" />
                <span>
                  This is a shared computer
                  <span className="block text-xs text-[var(--ink-3)]">Like the showroom counter PC: you are signed out after a few hours without use.</span>
                </span>
              </label>
              {error && (
                <div role="alert" className="p-3 rounded-lg bg-[var(--danger-soft)] border border-[var(--danger)]/20 text-[var(--danger)] text-sm" data-testid="login-error">
                  {error}
                </div>
              )}
              <button type="submit" disabled={submitting}
                      className="w-full py-3 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold disabled:opacity-60" data-testid="login-submit">
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>
            {googleId && (
              <div className="mt-6" data-testid="google-signin">
                <div className="flex items-center gap-3 text-xs text-[var(--ink-3)] mb-4">
                  <span className="flex-1 h-px bg-[var(--border)]" /> or <span className="flex-1 h-px bg-[var(--border)]" />
                </div>
                <div ref={googleBox} className="flex justify-center min-h-[44px]" />
                <p className="mt-2 text-[11px] text-center text-[var(--ink-3)]">Works with the Gmail address on your staff account.</p>
              </div>
            )}
            <div className="mt-8 text-xs text-[var(--ink-3)] text-center">
              Forgot your password? Ask your administrator for a temporary one.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
